import { supabase } from '../supabase';
import type { DocumentoEmail } from '@email/types.ts';

/**
 * I modelli di email: email messe da parte per riusarle.
 *
 * Come in emailService, il client va forzato una volta sola qui: le tabelle
 * email_* non sono ancora in database.types.ts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Dove ha senso proporre un modello. */
export type AmbitoModello = 'ovunque' | 'campagne' | 'lead';

export interface EmailTemplate {
  id: string;
  name: string;
  description: string | null;
  subject: string;
  preheader: string | null;
  document: DocumentoEmail;
  ambito: AmbitoModello;
  usi_count: number;
  ultimo_uso_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NuovoModello {
  name: string;
  description?: string | null;
  subject: string;
  preheader?: string | null;
  document: DocumentoEmail;
  ambito: AmbitoModello;
}

export const AMBITI: Record<AmbitoModello, { label: string; spiegazione: string }> = {
  ovunque: {
    label: 'Ovunque',
    spiegazione: 'Compare sia nelle campagne e sequenze, sia scrivendo a un lead.',
  },
  campagne: {
    label: 'Solo campagne e sequenze',
    spiegazione: 'Per le comunicazioni alla lista. Non compare fra i lead.',
  },
  lead: {
    label: 'Solo per i lead',
    spiegazione: 'Per rispondere a chi ci ha contattato, uno alla volta.',
  },
};

export const templateService = {
  /** Se `ambito` e' indicato, torna i modelli buoni per quel posto: quelli
   *  suoi piu' quelli validi ovunque. */
  async elenco(ambito?: AmbitoModello): Promise<EmailTemplate[]> {
    let query = db.from('email_templates').select('*').order('updated_at', { ascending: false });
    if (ambito && ambito !== 'ovunque') {
      query = query.in('ambito', [ambito, 'ovunque']);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as EmailTemplate[];
  },

  async crea(modello: NuovoModello): Promise<EmailTemplate> {
    const { data, error } = await db
      .from('email_templates')
      .insert({
        name: modello.name,
        description: modello.description ?? null,
        subject: modello.subject,
        preheader: modello.preheader ?? null,
        // Copia, non riferimento: ritoccare il modello domani non deve
        // cambiare una campagna partita ieri.
        document: modello.document,
        ambito: modello.ambito,
      })
      .select('*')
      .single();
    if (error) throw error;
    return data as EmailTemplate;
  },

  async aggiorna(
    id: string,
    campi: Partial<Omit<NuovoModello, 'document'> & { document: DocumentoEmail }>
  ): Promise<void> {
    const { error } = await db.from('email_templates').update(campi).eq('id', id);
    if (error) throw error;
  },

  async elimina(id: string): Promise<void> {
    const { error } = await db.from('email_templates').delete().eq('id', id);
    if (error) throw error;
  },

  /** Segna che il modello e' stato usato. Non blocca niente se fallisce: e'
   *  una statistica, non deve far saltare un invio. */
  async segnaUso(id: string): Promise<void> {
    const { error } = await db.rpc('email_modello_usato', { p_template_id: id });
    if (error) console.warn('Conteggio uso del modello non riuscito:', error.message);
  },
};
